// src/pages/Settings/Announcements.tsx
import { useEffect, useState } from 'react';
import { Megaphone, Plus, Pencil, Trash2, Clock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAccess } from '../../contexts/AccessContext';
import announcementApi from '../../api/announcementApi';
import { Announcement } from '../../types/announcement';
import { AnnouncementForm } from '../../components/AnnouncementForm';
import { usePermissionGuard } from '../../hooks/usePermissionGuard';
import { Popup } from '../../components/Popup';
import ConfirmDialog, { type ConfirmState } from '../../components/ConfirmDialog';

const fmtDate = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const isExpired = (a: Announcement) =>
  !!a.expires_at && new Date(a.expires_at).getTime() <= Date.now();

export default function Announcements() {
  const { user } = useAuth();
  const { canManage } = useAccess();
  const allowed = (user?.role || '').trim().toLowerCase() === 'admin' || canManage('set-announcements');

  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [saving, setSaving] = useState(false);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const { showError, showSuccess, popup, closePopup } = usePermissionGuard();

  const load = async () => {
    setLoading(true);
    try {
      setItems(await announcementApi.getAll());
    } catch {
      setItems([]); // silent empty-state fallback (no auto load-failure toast)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (a: Announcement) => { setEditing(a); setFormOpen(true); };

  const handleSubmit = async (data: Partial<Announcement>) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await announcementApi.update(editing.id, data);
        showSuccess('Saved', 'Announcement updated');
      } else {
        await announcementApi.create(data as Omit<Announcement, 'id'>);
        showSuccess('Created', 'Announcement published');
      }
      setFormOpen(false); setEditing(null);
      await load();
    } catch (err: any) {
      showError('Save failed', err?.response?.data?.detail || err?.message || 'Could not save the announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (a: Announcement) => {
    if (!a.id) return;
    const id = a.id;
    setConfirm({
      title: `Delete "${a.title}"?`,
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await announcementApi.remove(id);
          showSuccess('Deleted', 'Announcement removed');
          await load();
        } catch (err: any) {
          showError('Delete failed', err?.response?.data?.detail || err?.message || 'Could not delete the announcement');
        }
      },
    });
  };

  return (
    <div className="min-h-full bg-[#faf8f3]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#e8efe9] text-[#0f2d1f] flex items-center justify-center">
              <Megaphone className="w-[22px] h-[22px]" />
            </span>
            <div>
              <h1 className="text-[22px] font-semibold text-[#1a1a1a] leading-tight tracking-[-0.01em]">Announcements</h1>
              <p className="text-[13px] text-[#8a8580] mt-0.5">Messages that scroll in everyone's header</p>
            </div>
          </div>
          {allowed && (
            <button onClick={openCreate}
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 text-[13.5px] font-medium text-white bg-[#0f2d1f] rounded-xl hover:bg-[#0a1f15] active:scale-[0.98] transition shadow-sm">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">New announcement</span><span className="sm:hidden">New</span>
            </button>
          )}
        </div>

        <div className="bg-white border border-[#e8e3d8] rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(15,45,31,0.04)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-[#e8efe9] border-t-[#0f2d1f] animate-spin" />
              <p className="text-[13px] text-[#8a8580]">Loading…</p>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-24 px-6">
              <span className="w-16 h-16 rounded-2xl bg-[#e8efe9] text-[#0f2d1f] flex items-center justify-center mb-4">
                <Megaphone className="w-8 h-8" />
              </span>
              <h3 className="text-[17px] font-semibold text-[#1a1a1a]">No announcements yet</h3>
              <p className="text-[13.5px] text-[#8a8580] mt-1.5 max-w-sm">
                Create one and it will scroll in the top header for every team member.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#f0ece3]">
              {items.map((a) => {
                const off = a.is_active === false;
                const expired = isExpired(a);
                return (
                  <li key={a.id} className="flex items-start gap-4 px-5 py-4">
                    <span className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${off || expired ? 'bg-[#f1efe9] text-[#a39e95]' : 'bg-[#e8efe9] text-[#0f2d1f]'}`}>
                      {off ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14.5px] font-medium text-[#1a1a1a] truncate">{a.title}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                        {off && <span className="text-[11px] font-medium text-[#946c1a] bg-[#fef3c7] px-2 py-0.5 rounded-full">Off</span>}
                        {expired && <span className="text-[11px] font-medium text-[#b03b3b] bg-[#fdecea] px-2 py-0.5 rounded-full">Expired</span>}
                        {a.expires_at && !expired && (
                          <span className="inline-flex items-center gap-1 text-[12px] text-[#8a8580]">
                            <Clock className="w-3.5 h-3.5" /> Hides {fmtDate(a.expires_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {allowed && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openEdit(a)} title="Edit"
                          className="p-2 text-[#1f4030] hover:bg-[#e8efe9] rounded-lg transition"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(a)} title="Delete"
                          className="p-2 text-[#b03b3b] hover:bg-[#fdecea] rounded-lg transition"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {allowed && (
        <AnnouncementForm
          isOpen={formOpen}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={handleSubmit}
          initialData={editing}
          isLoading={saving}
        />
      )}

      <Popup isOpen={popup.isOpen} type={popup.type} title={popup.title} message={popup.message} onClose={closePopup} />

      {/* In-app confirmation dialog (replaces native window.confirm). */}
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
